import React, { useEffect, useMemo, useState } from "react";
import { Search, X, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Avatar, PlanBadge, StatusPill, OriginPill, SortableHeader, Th, EmptyState, Spinner,
  formatEUR, formatDate, cycleLabel, daysUntil,
} from "./adminUi";

export const DEFAULT_SUB_FILTERS = { q: "", plan: "all", cycle: "all", origin: "all", billing_status: "all", failed_only: false };
const PAGE = 10;

function FilterSelect({ label, value, onChange, options, testid, width = "w-36" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-11 ${width} rounded-md bg-white`} data-testid={testid}>
        <div className="text-left leading-tight">
          <div className="text-[10px] text-neutral-500">{label}</div>
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function AdminSubscribers({ initialFilters, onOpenUser, refreshKey, currentUserEmail }) {
  const [filters, setFilters] = useState({ ...DEFAULT_SUB_FILTERS, ...(initialFilters || {}) });
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);

  useEffect(() => { setFilters((f) => ({ ...f, ...(initialFilters || {}) })); }, [initialFilters]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.q) params.set("q", filters.q);
        ["plan","cycle","origin","billing_status"].forEach((k) => { if (filters[k] && filters[k] !== "all") params.set(k, filters[k]); });
        const { data } = await api.get(`/admin/users?${params.toString()}`);
        if (!alive) return;
        setUsers(data?.users || []);
        setTotal(data?.total || 0);
        setPage(1);
      } catch (err) { toast.error(err.response?.data?.detail || err.message); }
      finally { alive && setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [filters, refreshKey]);

  const rows = useMemo(() => {
    let arr = filters.failed_only
      ? users.filter((u) => u.billing_status === "past_due" || u.latest_payment?.status === "failed")
      : [...users];
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const ka = a[sort.key]; const kb = b[sort.key];
      if (ka == null && kb == null) return 0;
      if (ka == null) return 1;
      if (kb == null) return -1;
      if (typeof ka === "number" && typeof kb === "number") return (ka - kb) * dir;
      return String(ka).localeCompare(String(kb)) * dir;
    });
    return arr;
  }, [users, sort, filters.failed_only]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const visible = rows.slice((page - 1) * PAGE, page * PAGE);

  return (
    <div className="space-y-4" data-testid="admin-subscribers">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" strokeWidth={2} />
        <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Cerca nome, email o cliente…" className="pl-9 h-11 rounded-md bg-white" data-testid="admin-search-input" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="Piano" value={filters.plan} onChange={(v) => setFilters({ ...filters, plan: v })} testid="admin-filter-plan"
          options={[["all","Tutti i piani"],["free","Free"],["basic","Basic"],["pro","Pro"],["ai_manager","AI Manager"]]} />
        <FilterSelect label="Ciclo" value={filters.cycle} onChange={(v) => setFilters({ ...filters, cycle: v })} testid="admin-filter-cycle"
          options={[["all","Tutti"],["monthly","Mensile"],["yearly","Annuale"]]} width="w-32" />
        <FilterSelect label="Origine" value={filters.origin} onChange={(v) => setFilters({ ...filters, origin: v })} testid="admin-filter-origin"
          options={[["all","Tutte"],["stripe","Stripe"],["manual","Omaggio / Manuale"]]} width="w-40" />
        <FilterSelect label="Stato" value={filters.billing_status} onChange={(v) => setFilters({ ...filters, billing_status: v })} testid="admin-filter-status"
          options={[["all","Tutti"],["active","Attivo"],["past_due","Pagamento fallito"],["canceled","Cancellato"],["inactive","Inattivo"]]} width="w-40" />
        <button
          type="button"
          onClick={() => setFilters({ ...filters, failed_only: !filters.failed_only })}
          className={`h-11 px-4 rounded-md border text-sm inline-flex items-center gap-2 transition-colors ${filters.failed_only ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-white border-border text-neutral-700 hover:bg-neutral-50"}`}
          data-testid="admin-filter-failed"
        >
          <AlertCircle className="w-4 h-4" strokeWidth={1.8} /> Pagamenti falliti
        </button>
        {(filters.q || filters.plan !== "all" || filters.cycle !== "all" || filters.origin !== "all" || filters.billing_status !== "all" || filters.failed_only) && (
          <Button variant="ghost" className="h-11 rounded-md text-neutral-600" onClick={() => setFilters(DEFAULT_SUB_FILTERS)} data-testid="admin-reset-filters">
            <X className="w-3.5 h-3.5 mr-1" strokeWidth={2} /> Reimposta
          </Button>
        )}
      </div>

      <section className="bg-white border border-border rounded-md overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        {loading && users.length === 0 ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-neutral-50/60">
                  <SortableHeader label="Cliente" sortKey="name" sort={sort} setSort={setSort} />
                  <SortableHeader label="Piano" sortKey="plan" sort={sort} setSort={setSort} />
                  <Th>Ciclo / Durata</Th>
                  <SortableHeader label="Uso" sortKey="clients_created" sort={sort} setSort={setSort} />
                  <Th>Origine</Th>
                  <SortableHeader label="Stato" sortKey="billing_status" sort={sort} setSort={setSort} />
                  <Th>Ultimo pagamento</Th>
                  <SortableHeader label="Scadenza / Rinnovo" sortKey="current_period_end" sort={sort} setSort={setSort} />
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => {
                  const isOwner = u.email?.toLowerCase() === currentUserEmail?.toLowerCase();
                  const isManual = u.origin === "gift" || u.origin === "manual";
                  const end = isManual ? (u.manual_grant_expires_at || u.current_period_end) : u.current_period_end;
                  const d = daysUntil(end);
                  return (
                    <tr key={u.id} className="border-b border-border last:border-b-0 hover:bg-neutral-50/80 transition-colors cursor-pointer" data-testid={`admin-user-row-${u.id}`} onClick={() => onOpenUser(u.id)}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.name} email={u.email} />
                          <div className="min-w-0">
                            <div className="font-medium text-neutral-900 truncate max-w-[220px]">{u.name || "—"}{isOwner && <span className="ml-2 text-primary text-[10px] uppercase tracking-wider">Proprietario</span>}</div>
                            <div className="text-xs text-neutral-500 truncate max-w-[220px]">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4"><PlanBadge plan={u.plan} /></td>
                      <td className="py-3 px-4 text-neutral-700 whitespace-nowrap">{cycleLabel(u)}</td>
                      <td className="py-3 px-4 tabular-nums text-neutral-700 whitespace-nowrap">{u.clients_created} {u.clients_created === 1 ? "cliente" : "clienti"}</td>
                      <td className="py-3 px-4"><OriginPill origin={u.origin} /></td>
                      <td className="py-3 px-4"><StatusPill status={u.billing_status} suspended={u.suspended} periodEnd={u.current_period_end} /></td>
                      <td className="py-3 px-4 text-xs whitespace-nowrap">
                        {isManual ? <span className="text-neutral-600">Omaggio</span>
                          : u.latest_payment ? (
                            <div>
                              <div className={`tabular-nums font-medium ${u.latest_payment.status === "paid" ? "text-neutral-900" : "text-rose-700"}`}>{formatEUR(u.latest_payment.amount || 0)}</div>
                              <div className="text-neutral-500">{formatDate(u.latest_payment.at)}{u.latest_payment.status !== "paid" ? " · fallito" : ""}</div>
                            </div>
                          ) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="py-3 px-4 text-xs whitespace-nowrap">
                        {isManual && u.manual_grant_duration === "permanent" ? <span className="text-neutral-600">Senza scadenza</span>
                          : end ? (
                            <div>
                              <div className="tabular-nums text-neutral-900">{formatDate(end)}</div>
                              <div className={d !== null && d < 0 ? "text-rose-700" : d !== null && d <= 7 ? "text-amber-800" : "text-neutral-500"}>
                                {u.billing_status === "past_due" ? "Rinnovo fallito"
                                  : d !== null && d < 0 ? "Scaduto"
                                  : d !== null && d <= 7 ? `Tra ${d} giorni`
                                  : u.origin === "stripe" && u.billing_status === "active" ? "Si rinnova" : ""}
                              </div>
                            </div>
                          ) : <span className="text-neutral-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && <tr><td colSpan={8}><EmptyState>Nessun utente trovato.</EmptyState></td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs text-neutral-500">
          <span data-testid="admin-users-counter">Mostra {visible.length} di {rows.length} risultati{total !== rows.length ? ` (${total} totali)` : ""}</span>
          <div className="flex items-center gap-1">
            <button type="button" className="p-1.5 rounded-md border border-border disabled:opacity-40 hover:bg-neutral-50" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-page-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
            {Array.from({ length: pages }).slice(0, 7).map((_, i) => (
              <button key={i} type="button" onClick={() => setPage(i + 1)} className={`w-8 h-8 rounded-md border text-xs ${page === i + 1 ? "bg-neutral-100 border-neutral-300 text-neutral-900" : "border-border hover:bg-neutral-50"}`}>{i + 1}</button>
            ))}
            <button type="button" className="p-1.5 rounded-md border border-border disabled:opacity-40 hover:bg-neutral-50" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="admin-page-next"><ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </section>
    </div>
  );
}
